import React, { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { Dialog } from '@components/Dialog';
import { Button } from '@components/Button';
import { stockAnalyticsService } from '../../services/stockAnalyticsService';
import { StockAnalyticsParameters } from '../../types/stockAnalytics';

interface StockAnalyticsParametersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

interface FieldDef {
  key: keyof StockAnalyticsParameters;
  label: string;
  hint: string;
  /** Como o parâmetro entra nas fórmulas e a direção do efeito na compra sugerida. */
  impact?: string;
  step?: string;
}

const FIELDS: FieldDef[] = [
  {
    key: 'service_level_percent',
    label: 'Nível de serviço (%)',
    hint: 'Probabilidade de não faltar produto durante a reposição (define o fator Z do estoque de segurança). Padrão: 95.',
    impact: '↑ aumenta o fator Z → ↑ estoque de segurança → ↑ ponto de pedido e Máx → ↑ compra sugerida. Mais conservador (menos ruptura, mais capital parado); ↓ reduz as sugestões.',
    step: '0.5',
  },
  {
    key: 'review_cycle_days',
    label: 'Ciclo de revisão (dias)',
    hint: 'Intervalo típico entre pedidos de compra ao mesmo fornecedor. Padrão: 30.',
    impact: 'Entra em Máx = demanda·(lead time + ciclo) + segurança. ↑ eleva o Máx → ↑ compra sugerida (pedidos maiores e menos frequentes). Não altera o Mín.',
  },
  {
    key: 'default_lead_time_days',
    label: 'Lead time padrão (dias)',
    hint: 'Usado quando o produto/fornecedor não tem histórico de recebimentos. Padrão: 30.',
    impact: 'Só afeta produtos sem histórico de lead time. ↑ eleva estoque de segurança, ponto de pedido e Máx → ↑ compra sugerida desses itens.',
  },
  {
    key: 'slow_moving_days',
    label: 'Baixo giro a partir de (dias sem venda)',
    hint: 'Padrão: 90.',
    impact: 'Classifica giro (rótulo). Não zera a compra sozinho, mas ↑ afrouxa a classificação (menos itens marcados como baixo giro).',
  },
  {
    key: 'very_slow_moving_days',
    label: 'Muito baixo giro a partir de (dias)',
    hint: 'Padrão: 180.',
    impact: 'Itens "muito baixo giro" têm compra sugerida forçada a 0. ↑ deste limiar → menos itens zerados (mais elegíveis a compra); ↓ → mais itens zerados.',
  },
  {
    key: 'idle_days',
    label: 'Parado a partir de (dias)',
    hint: 'Padrão: 365.',
    impact: 'Itens "parados" têm compra sugerida forçada a 0. ↑ → menos itens zerados; ↓ → mais itens zerados.',
  },
  {
    key: 'excess_coverage_days',
    label: 'Excesso acima de (dias de cobertura)',
    hint: 'Cobertura acima disso marca o produto como excesso de estoque. Padrão: 180.',
    impact: 'Item marcado como excesso tem compra sugerida forçada a 0. ↑ → menos itens marcados como excesso (mais elegíveis); ↓ → mais itens zerados.',
  },
  {
    key: 'demand_window_months',
    label: 'Janela de demanda (meses)',
    hint: 'Meses de histórico usados na média/desvio da demanda. Padrão: 12.',
    impact: 'Base da demanda média e do desvio → afeta segurança, ponto de pedido e Máx. Janela maior suaviza (inclui meses antigos); menor reage mais rápido a mudanças recentes.',
  },
  {
    key: 'abc_window_months',
    label: 'Janela ABC (meses)',
    hint: 'Meses de receita usados na curva ABC. Padrão: 12.',
    impact: 'Apenas classificação/priorização ABC — NÃO entra na conta da compra sugerida.',
  },
  {
    key: 'abc_a_threshold_percent',
    label: 'Limiar classe A (% receita acumulada)',
    hint: 'Padrão: 80.',
    impact: 'Apenas rótulo ABC — não altera a compra sugerida. ↑ do limiar → menos itens na classe A.',
    step: '0.5',
  },
  {
    key: 'abc_b_threshold_percent',
    label: 'Limiar classe B (% receita acumulada)',
    hint: 'Padrão: 95.',
    impact: 'Apenas rótulo ABC — não altera a compra sugerida.',
    step: '0.5',
  },
  {
    key: 'xyz_x_threshold',
    label: 'Limiar classe X (coef. de variação)',
    hint: 'Demanda estável. Padrão: 0,5.',
    impact: 'Apenas rótulo XYZ (estabilidade da demanda) — não altera diretamente a fórmula de compra.',
    step: '0.05',
  },
  {
    key: 'xyz_y_threshold',
    label: 'Limiar classe Y (coef. de variação)',
    hint: 'Demanda variável. Padrão: 1,0.',
    impact: 'Apenas rótulo XYZ — não altera diretamente a fórmula de compra.',
    step: '0.05',
  },
  {
    key: 'min_seasonality_months',
    label: 'Histórico mínimo p/ sazonalidade (meses)',
    hint: 'Abaixo disso os índices sazonais ficam neutros. Padrão: 24.',
    impact: 'Com histórico abaixo deste mínimo, o fator sazonal fica neutro (1,0). ↓ deste mínimo → sazonalidade aplicada a mais produtos, ajustando a demanda prevista conforme o mês do horizonte de compra.',
  },
  {
    key: 'lead_time_window_months',
    label: 'Janela de lead time (meses)',
    hint: 'Recebimentos considerados no cálculo do lead time. Padrão: 24.',
    impact: 'Afeta a média e o desvio do lead time → estoque de segurança, ponto de pedido e Máx. Janela maior = mais amostras e estimativa mais estável.',
  },
  {
    key: 'min_lead_time_samples',
    label: 'Amostras mínimas de lead time',
    hint: 'Mínimo de recebimentos para usar a média do produto/fornecedor. Padrão: 3.',
    impact: 'Abaixo deste mínimo o cálculo cai para o lead time global/padrão. ↑ exige mais amostras → mais produtos usam o lead time global/padrão em vez do próprio.',
  },
  {
    key: 'monthly_recalc_window_months',
    label: 'Janela de recálculo mensal (meses)',
    hint: 'Meses recentes recalculados a cada execução do batch. Padrão: 3.',
    impact: 'Só define quantos meses recentes o batch reprocessa (desempenho/correção retroativa). Não altera a fórmula por produto.',
  },
];

/**
 * Modal de manutenção dos parâmetros globais de cálculo da análise de estoque
 * (restrito a admin via stock_analytics.manage_parameters). Alterações passam
 * a valer na PRÓXIMA execução do processo batch.
 */
export const StockAnalyticsParametersModal: React.FC<StockAnalyticsParametersModalProps> = ({ isOpen, onClose, onSaved }) => {
  const [params, setParams] = useState<StockAnalyticsParameters | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setLoading(true);
    stockAnalyticsService
      .getParameters()
      .then(setParams)
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar parâmetros'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleChange = (key: keyof StockAnalyticsParameters, value: string) => {
    if (!params) return;
    setParams({ ...params, [key]: value === '' ? 0 : Number(value) });
  };

  const handleSave = async () => {
    if (!params) return;
    setSaving(true);
    setError(null);
    try {
      await stockAnalyticsService.updateParameters(params);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar parâmetros');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Parâmetros da análise de estoque"
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading || !params}>
            {saving ? 'Salvando…' : 'Salvar parâmetros'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          Os parâmetros valem para todos os produtos e passam a valer na <strong>próxima execução</strong> do
          processo batch de análise de estoque.
        </p>

        {error && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        {loading && <p className="text-sm text-slate-500">Carregando parâmetros…</p>}

        {!loading && params && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[55vh] overflow-y-auto pr-1">
            {FIELDS.map((field) => (
              <div key={field.key}>
                <label className="block text-xs font-medium text-slate-700 mb-1">{field.label}</label>
                <input
                  type="number"
                  step={field.step ?? '1'}
                  min={0}
                  value={params[field.key] as number}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  className="input-field w-full"
                />
                <p className="text-[11px] text-slate-400 mt-1">{field.hint}</p>
                {field.impact && (
                  <p className="text-[11px] text-slate-500 mt-1 flex items-start gap-1">
                    <TrendingUp size={12} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span>{field.impact}</span>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default StockAnalyticsParametersModal;
